import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { buildServiceUrl, buildBookingUrl, buildEventTypeUrl, scopePath } from "../utils/urlUtils"
import { useWebsiteBuilderV2 } from "../WebsiteBuilderV2Context"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const HeaderSectionDesktop = ({ section, practice, theme, highlightedField, transparent = false }) => {
  // Try to get pages from context (for builder mode) or from practice object (for public mode)
  let contextPages = []
  try {
    const context = useWebsiteBuilderV2()
    contextPages = context?.pages || []
  } catch (error) {
    // Context not available (public mode), use practice pages only
    // This is expected behavior when not in builder mode
  }

  const practicePages = practice?.pages || []
  const availablePages = contextPages.length > 0 ? contextPages : practicePages

  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const showLogo = settings.showLogo || true
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || theme?.header?.buttonText || "Book Now"
  const ctaAction = theme?.header?.ctaAction || settings.ctaAction || "booking"
  const ctaTarget = theme?.header?.ctaTarget || settings.ctaTarget || ""
  const showPromoCTA = settings.showPromoCTA || false
  const promoCTAText = settings.promoCTAText || "Free discovery call"
  const promoCTAAction = settings.promoCTAAction || "booking"
  const showNavigation = settings.showNavigation || true
  const showSocialLinks = settings.showSocialLinks || true

  // Promo banner settings from HeaderForm
  const showPromoBanner = theme?.header?.showPromoBanner || false
  const promoBannerText = theme?.header?.promoHeadline || "This is some exciting new event that we are offering!"
  const promoBannerLinkText = theme?.header?.promoButtonText || "Learn more"
  const promoBannerAction = theme?.header?.promoCtaType || "page"
  const promoBannerTarget = theme?.header?.promoService || ""
  const promoBannerEvent = theme?.header?.promoEvent || ""

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          // Use the practice slug to navigate to the correct page
          const targetUrl = scopePath(`/${ctaTarget}`, practice)
          window.location.href = targetUrl
        }
        break
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const handlePromoCTAClick = () => {
    switch (promoCTAAction) {
      case "page":
        if (settings.promoCTATarget) {
          const targetElement = document.getElementById(settings.promoCTATarget)
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: "smooth" })
          }
        }
        break
      case "service":
        if (settings.promoCTATarget) {
          window.open(buildServiceUrl(settings.promoCTATarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const handlePromoBannerClick = () => {
    switch (promoBannerAction) {
      case "page":
        if (promoBannerTarget) {
          // Use the practice slug to navigate to the correct page
          const targetUrl = scopePath(`/${promoBannerTarget}`, practice)
          window.location.href = targetUrl
        }
        break
      case "service":
        if (promoBannerTarget) {
          // It's a service, use the service URL
          window.open(buildServiceUrl(promoBannerTarget, practice), "_blank")
        }
        break
      case "event":
        if (promoBannerEvent) {
          // Build event URL using the event type slug
          const eventUrl = buildEventTypeUrl(promoBannerEvent, practice)
          window.location.href = eventUrl
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  // Generate navigation links from practice's pages
  const generateNavigationLinks = () => {
    if (!availablePages || availablePages.length === 0) {
      // Fallback to default links if no pages available
      const defaultLinks = [
        { text: "Services", href: scopePath("/services", practice) },
        { text: "About", href: scopePath("/about", practice) },
        { text: "Reviews", href: scopePath("/review", practice) },
        { text: "Contact", href: scopePath("/contact", practice) }
      ]

      // Add Home link only if showHome is enabled
      const showHome = theme?.header?.showHome || false
      return showHome ? [{ text: "Home", href: scopePath("/", practice) }, ...defaultLinks] : defaultLinks
    }

    // Start with Home link if enabled, then add other pages
    const showHome = theme?.header?.showHome || false
    const homeLink = showHome ? { text: "Home", href: scopePath("/", practice) } : null

    // Filter out the home page and hidden pages, then create navigation links from available pages
    const pageLinks = availablePages
      .filter((page) => page.defaultPageType !== "home" && !page.hidden) // Exclude home page and hidden pages from navigation
      .sort((a, b) => a.id - b.id) // Sort by ID (creation order) - treat all pages equally
      .map((page) => ({
        text: page.title,
        href: scopePath(`/${page.slug}`, practice)
      }))

    return homeLink ? [homeLink, ...pageLinks] : pageLinks
  }

  const navigationLinks = generateNavigationLinks()

  // Use theme navigation links if available, otherwise use dynamic page links
  const headerNavigationLinks = theme?.header?.navigationLinks || navigationLinks

  const socialLinks = [
    { name: "Instagram", icon: "instagram", url: practice?.socialLinks?.instagram },
    { name: "TikTok", icon: "tiktok", url: practice?.socialLinks?.tiktok },
    { name: "X", icon: "x", url: practice?.socialLinks?.x },
    { name: "Facebook", icon: "facebook", url: practice?.socialLinks?.facebook },
    { name: "YouTube", icon: "youtube", url: practice?.socialLinks?.youtube },
    { name: "LinkedIn", icon: "linkedin", url: practice?.socialLinks?.linkedin }
  ]

  const renderSocialIcon = (iconName) => {
    const iconClass = "h-3 w-3"
    switch (iconName) {
      case "instagram":
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        )
      case "tiktok":
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
          </svg>
        )
      case "x":
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        )
      case "facebook":
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        )
      case "youtube":
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
        )
      case "linkedin":
        return (
          <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <div className="relative z-10">
      {/* Promo Banner - Separate from header section */}
      {showPromoBanner && (
        <div
          className="flex w-full items-center justify-center px-8 py-4 text-center"
          style={{
            backgroundColor: theme?.colors?.bgLight || "#F4ECE9",
            color: theme?.colors?.promoBannerText || theme?.colors?.textDark || "#000000"
          }}>
          <div className="flex items-center gap-2">
            <span
              className="text-base font-normal leading-6"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontSize: "16px",
                lineHeight: "24px"
              }}>
              {promoBannerText}{" "}
              <button
                onClick={handlePromoBannerClick}
                className="font-semibold underline hover:no-underline"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color:
                    theme?.colors?.promoBannerLink || theme?.colors?.primary || theme?.colors?.textDark || "#000000"
                }}>
                {promoBannerLinkText}
              </button>
            </span>
          </div>
        </div>
      )}

      {/* Main Header Section */}
      <section
        className={twMerge(
          "relative flex items-center justify-center",
          transparent ? "bg-transparent" : "",
          isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
        )}
        style={{
          backgroundColor: transparent ? "transparent" : theme?.colors?.background || "#FFFFFF",
          minHeight: "72px",
          height: showLogo ? `${(theme?.header?.logoSize || 72) + 20}px` : "72px"
        }}>
        <AnimatedSection className="w-full px-8 py-3">
          <div className="flex items-center justify-between">
            {/* Left Side - Logo */}
            {showLogo && (
              <div className="flex items-center">
                <a href={scopePath("/", practice)} className="cursor-pointer transition-opacity hover:opacity-75">
                  {practice?.logoUrl || theme?.header?.logo ? (
                    <img
                      src={practice?.logoUrl || theme.header.logo}
                      alt={practice?.name || "Practice Logo"}
                      className="w-auto object-contain"
                      style={{
                        maxWidth: `${theme?.header?.logoSize || 72}px`,
                        height: "auto",
                        borderRadius: theme?.borderRadius || "0px"
                      }}
                    />
                  ) : (
                    <span
                      className="text-sm font-bold lowercase tracking-tight"
                      style={{
                        fontFamily: theme?.fonts?.heading || "Domine, serif",
                        fontWeight: "700",
                        fontSize: "14.47px",
                        lineHeight: "1em",
                        letterSpacing: "-4%",
                        color: transparent ? "#FFFFFF" : theme?.colors?.textDark || "#000000"
                      }}>
                      {practice?.name || "Practice Name"}
                    </span>
                  )}
                </a>
              </div>
            )}

            {/* Right Side - Navigation and CTA with fixed positioning */}
            <div className="flex items-center gap-4">
              {/* Navigation Links */}
              {showNavigation && (
                <nav className="flex items-center gap-4">
                  {headerNavigationLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.href}
                      className="whitespace-nowrap px-4 py-3 text-base transition-colors hover:opacity-75"
                      style={{
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                        fontWeight: "400",
                        fontSize: "16px",
                        lineHeight: "24px",
                        color: transparent ? "#FFFFFF" : theme?.colors?.textDark || "#000000"
                      }}>
                      {link.text}
                    </a>
                  ))}
                </nav>
              )}
              {/* Social Links */}
              {showSocialLinks && (
                <div className="flex items-center gap-3">
                  {socialLinks.map(
                    (social) =>
                      social.url && (
                        <a
                          key={social.name}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-3 w-3 items-center justify-center transition-colors hover:opacity-75"
                          style={{ color: transparent ? "#FFFFFF" : theme?.colors?.textDark || "#000000" }}>
                          {renderSocialIcon(social.icon)}
                        </a>
                      )
                  )}
                </div>
              )}
              {showPromoCTA && (
                <button
                  onClick={handlePromoCTAClick}
                  className="rounded border px-3 py-1 text-xs font-medium transition-colors hover:bg-white hover:text-teal"
                  style={{
                    borderColor: theme?.colors?.textLight || "#FFFFFF",
                    color: theme?.colors?.textLight || "#FFFFFF"
                  }}>
                  {promoCTAText}
                </button>
              )}
              {/* Main CTA */}
              {showCTA && (
                <button
                  onClick={handleCTAClick}
                  className={twMerge(
                    "inline-flex items-center justify-center whitespace-nowrap px-[40px] py-[12px] text-base font-normal transition-all hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      color: theme?.colors?.textLight || "#F4ECE9",
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      fontWeight: "400",
                      fontSize: "16px",
                      lineHeight: "24px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                  }}>
                  {ctaText}
                </button>
              )}
            </div>
          </div>
        </AnimatedSection>
      </section>
    </div>
  )
}

export default HeaderSectionDesktop
